
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import BottomNavigation from '../../components/feature/BottomNavigation';

interface Order {
  orderNumber: number;
  items: any[];
  deliveryInfo: any;
  paymentMethod: string;
  total: number;
  date: string;
  status: 'confirmed' | 'preparing' | 'shipping' | 'delivered' | 'cancelled';
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    // تحميل الطلبات من localStorage
    const savedOrders = localStorage.getItem('orders');
    if (savedOrders) {
      setOrders(JSON.parse(savedOrders));
    }
  }, []);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'confirmed':
        return { text: 'مؤكد', color: 'bg-blue-100 text-blue-800', icon: 'ri-check-line' };
      case 'preparing':
        return { text: 'قيد التحضير', color: 'bg-yellow-100 text-yellow-800', icon: 'ri-time-line' };
      case 'shipping':
        return { text: 'قيد التوصيل', color: 'bg-purple-100 text-purple-800', icon: 'ri-truck-line' };
      case 'delivered':
        return { text: 'تم التسليم', color: 'bg-green-100 text-green-800', icon: 'ri-check-double-line' };
      case 'cancelled':
        return { text: 'ملغي', color: 'bg-red-100 text-red-800', icon: 'ri-close-line' };
      default:
        return { text: 'غير معروف', color: 'bg-gray-100 text-gray-800', icon: 'ri-question-line' };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleReorder = (order: Order) => {
    // إضافة منتجات الطلب إلى السلة
    const cartItems = order.items.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      quantity: item.quantity
    }));
    
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    navigate('/cart');
  };

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header cartItemsCount={0} />
        
        <main className="pt-20 pb-20 px-4">
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-list-line text-4xl text-[#D4AF37]"></i>
            </div>
            <h2 className="text-xl font-bold text-[#333333] mb-4" dir="rtl">
              لا توجد طلبات
            </h2>
            <p className="text-[#777777] mb-8" dir="rtl">
              لم تقم بأي طلبات بعد
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#D4AF37] text-white px-8 py-3 rounded-full font-medium"
            >
              ابدأ التسوق
            </button>
          </div>
        </main>

        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header cartItemsCount={0} />
      
      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white rounded-t-xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between" dir="rtl">
                <h3 className="text-lg font-bold text-[#333333]">
                  تفاصيل الطلب #{selectedOrder.orderNumber}
                </h3>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-[#333333]"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Order Status */}
              <div className="text-center">
                <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusInfo(selectedOrder.status).color}`}>
                  <i className={`${getStatusInfo(selectedOrder.status).icon} ml-2`}></i>
                  {getStatusInfo(selectedOrder.status).text}
                </div>
              </div>
              
              {/* Order Items */}
              <div>
                <h4 className="font-bold text-[#333333] mb-3 text-right" dir="rtl">
                  المنتجات ({selectedOrder.items.length})
                </h4>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg" dir="rtl">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h5 className="font-medium text-[#333333] text-sm">{item.name}</h5>
                        <p className="text-xs text-[#777777]">
                          {item.quantity} × {item.price} ر.س
                        </p>
                      </div>
                      <span className="font-bold text-[#4B2E05]">
                        {(item.price * item.quantity).toFixed(0)} ر.س
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Delivery Info */}
              <div>
                <h4 className="font-bold text-[#333333] mb-3 text-right" dir="rtl">
                  معلومات التوصيل
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2" dir="rtl">
                  <p><span className="font-medium">الاسم:</span> {selectedOrder.deliveryInfo.name}</p>
                  <p><span className="font-medium">الجوال:</span> {selectedOrder.deliveryInfo.phone}</p>
                  <p><span className="font-medium">العنوان:</span> {selectedOrder.deliveryInfo.address}</p>
                  <p><span className="font-medium">المدينة:</span> {selectedOrder.deliveryInfo.city}</p>
                </div>
              </div>
              
              {/* Total */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-bold" dir="rtl">
                  <span>المجموع الكلي</span>
                  <span className="text-[#4B2E05]">{selectedOrder.total.toFixed(0)} ر.س</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleReorder(selectedOrder)}
                  className="flex-1 bg-[#D4AF37] text-white py-3 rounded-lg font-medium"
                >
                  إعادة الطلب
                </button>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="flex-1 bg-gray-200 text-[#333333] py-3 rounded-lg font-medium"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <main className="pt-20 pb-20 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" dir="rtl">
          <h1 className="text-2xl font-bold text-[#333333]">
            طلباتي
          </h1>
          <span className="text-sm text-[#777777]">
            {orders.length} طلب
          </span>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {orders.map((order) => {
            const statusInfo = getStatusInfo(order.status);
            return (
              <div
                key={order.orderNumber}
                onClick={() => handleOrderClick(order)}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3" dir="rtl">
                  <div>
                    <h3 className="font-bold text-[#333333]">
                      طلب #{order.orderNumber}
                    </h3>
                    <p className="text-sm text-[#777777]">
                      {formatDate(order.date)}
                    </p>
                  </div>
                  <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    <i className={`${statusInfo.icon} ml-1`}></i>
                    {statusInfo.text}
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-3" dir="rtl">
                  <div className="flex items-center">
                    <span className="text-sm text-[#777777]">
                      {order.items.length} منتج
                    </span>
                    <span className="mx-2 text-[#777777]">•</span>
                    <span className="text-sm font-bold text-[#4B2E05]">
                      {order.total.toFixed(0)} ر.س
                    </span>
                  </div>
                  <i className="ri-arrow-left-s-line text-[#777777]"></i>
                </div>
                
                {/* Order Items Preview */}
                <div className="flex items-center gap-2 overflow-x-auto" dir="rtl">
                  {order.items.slice(0, 3).map((item, index) => (
                    <img
                      key={index}
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                    />
                  ))}
                  {order.items.length > 3 && (
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xs text-[#777777]">+{order.items.length - 3}</span>
                    </div>
                  )}
                </div>
                
                {/* Quick Actions */}
                <div className="flex gap-2 mt-4">
                  {order.status === 'delivered' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReorder(order);
                      }}
                      className="flex-1 bg-[#D4AF37]/10 text-[#D4AF37] py-2 rounded-lg text-sm font-medium hover:bg-[#D4AF37]/20 transition-colors"
                    >
                      إعادة الطلب
                    </button>
                  )}
                  {(order.status === 'confirmed' || order.status === 'preparing' || order.status === 'shipping') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOrderClick(order);
                      }}
                      className="flex-1 bg-[#D4AF37]/10 text-[#D4AF37] py-2 rounded-lg text-sm font-medium hover:bg-[#D4AF37]/20 transition-colors"
                    >
                      تتبع الطلب
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="bg-[#D4AF37]/10 rounded-xl p-6 mt-6">
          <h3 className="text-lg font-bold text-[#333333] mb-4 text-right" dir="rtl">
            تحتاج مساعدة؟
          </h3>
          
          <div className="space-y-3" dir="rtl">
            <button className="w-full flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <i className="ri-phone-line text-[#D4AF37] text-xl ml-3"></i>
                <span className="font-medium text-[#333333]">اتصل بخدمة العملاء</span>
              </div>
              <i className="ri-arrow-left-s-line text-[#777777]"></i>
            </button>
            
            <button className="w-full flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <i className="ri-whatsapp-line text-[#D4AF37] text-xl ml-3"></i>
                <span className="font-medium text-[#333333]">تواصل عبر واتساب</span>
              </div>
              <i className="ri-arrow-left-s-line text-[#777777]"></i>
            </button>
          </div>
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
}
